"use client";

import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { type FormEvent, useRef, useState } from "react";

type SubmissionState = "idle" | "submitting" | "success" | "error";

function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `teacher-${Date.now()}-${Math.random().toString(36).slice(2, 18)}`;
}

export function TeacherPartnerApplicationForm() {
  const posthog = usePostHog();
  const hasTrackedStart = useRef(false);
  const idempotencyKey = useRef(createIdempotencyKey());
  const [submissionState, setSubmissionState] = useState<SubmissionState>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleStart = () => {
    if (hasTrackedStart.current) return;
    hasTrackedStart.current = true;
    posthog?.capture("teacher_partner_application_started", { placement: "teach_page" });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmissionState("submitting");
    setErrorMessage("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = {
      idempotencyKey: idempotencyKey.current,
      name: formData.get("name"),
      email: formData.get("email"),
      teachingLanguages: formData.get("teachingLanguages"),
      experience: formData.get("experience"),
      qualification: formData.get("qualification"),
      partnerMode: formData.get("partnerMode"),
      location: formData.get("location"),
      profileUrl: formData.get("profileUrl"),
      note: formData.get("note"),
      website: formData.get("website"),
      sourcePath: window.location.pathname,
    };

    try {
      const response = await fetch("/api/teacher-partners/apply", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const result = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(result?.error || "We could not send your application.");
      }

      setSubmissionState("success");
      form.reset();
      posthog?.capture("teacher_partner_application_submitted", {
        placement: "teach_page",
        partner_mode: typeof payload.partnerMode === "string" ? payload.partnerMode : "unknown",
      });
    } catch (error) {
      setSubmissionState("error");
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "We could not send your application. Please try again."
      );
    }
  };

  if (submissionState === "success") {
    return (
      <div
        className="flex min-h-[420px] flex-col items-center justify-center rounded-[28px] border border-[#e8e1d8] bg-white p-8 text-center"
        role="status"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#fff0e7] text-[#c84f1f]">
          <CheckCircle2 className="h-7 w-7" aria-hidden />
        </span>
        <h3 className="mt-6 text-2xl font-black text-[#171717]">Application received.</h3>
        <p className="mt-3 max-w-md leading-7 text-[#6f6b64]">
          We will review your experience, language and preferred way of working, then reply by
          email.
        </p>
      </div>
    );
  }

  const fieldClass =
    "mt-2 min-h-12 w-full rounded-2xl border border-[#dcd4ca] bg-[#fffdf9] px-4 py-3 text-[15px] text-[#171717] outline-none transition placeholder:text-[#8a857e] focus:border-[#f26b2d] focus:ring-4 focus:ring-[#f26b2d]/10";

  return (
    <form
      onSubmit={handleSubmit}
      onFocus={handleStart}
      className="rounded-[28px] border border-[#e8e1d8] bg-white p-5 shadow-[0_20px_60px_rgba(30,20,10,0.08)] sm:p-8"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="text-sm font-bold text-[#3b3731]">
          Your name <span className="text-[#c84f1f]">*</span>
          <input
            className={fieldClass}
            name="name"
            type="text"
            autoComplete="name"
            required
            maxLength={100}
            placeholder="Alex Morgan"
          />
        </label>
        <label className="text-sm font-bold text-[#3b3731]">
          Email <span className="text-[#c84f1f]">*</span>
          <input
            className={fieldClass}
            name="email"
            type="email"
            autoComplete="email"
            required
            maxLength={254}
            placeholder="alex@example.com"
          />
        </label>
      </div>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <label className="text-sm font-bold text-[#3b3731]">
          Language or languages you teach <span className="text-[#c84f1f]">*</span>
          <input
            className={fieldClass}
            name="teachingLanguages"
            type="text"
            required
            maxLength={200}
            placeholder="French and Dutch"
          />
        </label>
        <label className="text-sm font-bold text-[#3b3731]">
          Location or time zone <span className="text-[#c84f1f]">*</span>
          <input
            className={fieldClass}
            name="location"
            type="text"
            required
            maxLength={160}
            placeholder="Amsterdam, CET"
          />
        </label>
      </div>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <label className="text-sm font-bold text-[#3b3731]">
          Teaching experience <span className="text-[#c84f1f]">*</span>
          <select className={fieldClass} name="experience" defaultValue="" required>
            <option value="" disabled>
              Choose one
            </option>
            <option value="under_2_years">Under 2 years</option>
            <option value="two_to_four_years">2 to 4 years</option>
            <option value="five_plus_years">5+ years</option>
          </select>
        </label>
        <label className="text-sm font-bold text-[#3b3731]">
          How would you like to start? <span className="text-[#c84f1f]">*</span>
          <select className={fieldClass} name="partnerMode" defaultValue="" required>
            <option value="" disabled>
              Choose one
            </option>
            <option value="bring_my_learners">Bring my own learners</option>
            <option value="teach_chickytutor_learners">
              Teach learners ChickyTutor introduces
            </option>
            <option value="both">I am open to both</option>
          </select>
        </label>
      </div>

      <label className="mt-5 block text-sm font-bold text-[#3b3731]">
        Teaching qualification or relevant experience
        <input
          className={fieldClass}
          name="qualification"
          type="text"
          maxLength={240}
          placeholder="For example, CELTA, NT2 or five years teaching adults"
        />
      </label>

      <label className="mt-5 block text-sm font-bold text-[#3b3731]">
        Public profile or website
        <input
          className={fieldClass}
          name="profileUrl"
          type="url"
          inputMode="url"
          maxLength={500}
          placeholder="https://"
        />
      </label>

      <label className="mt-5 block text-sm font-bold text-[#3b3731]">
        What would make this partnership useful to you?
        <textarea
          className={`${fieldClass} min-h-32 resize-y`}
          name="note"
          maxLength={1600}
          placeholder="Tell us about your learners, current classes or the kind of course you would like to teach."
        />
      </label>

      <label className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden>
        Website
        <input name="website" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      </label>

      {submissionState === "error" && (
        <p
          className="mt-5 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
          role="alert"
        >
          {errorMessage}
        </p>
      )}

      <button
        type="submit"
        disabled={submissionState === "submitting"}
        className="commercial-button commercial-button-primary mt-6 w-full disabled:cursor-wait disabled:opacity-70"
      >
        {submissionState === "submitting" ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            Sending application
          </>
        ) : (
          <>
            Apply to teach with ChickyTutor
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
          </>
        )}
      </button>
      <p className="mt-4 text-center text-xs leading-5 text-[#6f6b64]">
        An application is the start of a conversation. It does not create a teaching contract or
        confirm a commission agreement.
      </p>
    </form>
  );
}
